<div class="relative">
  <!-- Loading overlay -->
  @if (loading) {
    <div class="absolute inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-2xl">
      <div class="flex flex-col items-center space-y-3 bg-white dark:bg-gray-800 px-6 py-4 rounded-lg shadow-lg">
        <div class="animate-spin h-6 w-6 border-3 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    </div>
  }

  @if (crmSalesFlowEnabled) {
    <!-- TVG-111: New 7-column CRM Sales Flow board -->
    <div class="flex items-center justify-between gap-4 px-2 py-3">
      <!-- TVG-300: "Board" reads as "Deals" throughout, matching the sidebar. -->
      <h2 class="text-lg font-semibold text-gray-800 dark:text-white/90">Deals</h2>

      <div class="flex items-center gap-3">
        <!-- TVG-331: book a viewing for any venue without first finding the
             card — the dialog picks the contact and the event. -->
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-2 text-sm font-medium text-white shadow-theme-xs transition-colors hover:bg-brand-600"
          data-testid="book-new-viewing-button"
          (click)="openBookNewViewing()"
        >
          <lucide-icon [name]="Calendar" class="h-4 w-4"></lucide-icon>
          <span>Book New Viewing</span>
        </button>

        <!-- TVG-237: staff help panel trigger -->
        <app-help-panel page="board"></app-help-panel>

        <!-- Venue filter -->
        <div class="relative">
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white/80"
          (click)="toggleVenueFilterDropdown()"
        >
          <span>{{ venueFilterLabel() }}</span>
          <lucide-icon [name]="ChevronDown" class="h-4 w-4"></lucide-icon>
        </button>

        @if (venueFilterOpen) {
          <div
            class="absolute right-0 z-30 mt-2 w-64 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800"
          >
            <div class="flex items-center justify-between px-2 py-1">
              <span class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Filter by venue</span>
              <button
                type="button"
                class="text-xs text-brand-500 hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
                [disabled]="selectedVenueIds.size === 0"
                (click)="clearVenueFilter()"
              >
                Clear
              </button>
            </div>
            <div class="max-h-64 overflow-y-auto">
              @for (venue of venueOptions; track venue.id) {
                <label class="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-white/80 dark:hover:bg-white/5">
                  <input
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    [checked]="isVenueSelected(venue.id)"
                    (change)="toggleVenueFilter(venue.id)"
                  />
                  <span>{{ venue.name }}</span>
                </label>
              } @empty {
                <div class="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">No venues available.</div>
              }
            </div>
          </div>
        }
      </div>
      </div>
    </div>

    <div
      class="grid grid-cols-1 gap-3 border-t border-gray-200 pt-3 dark:border-gray-800 sm:grid-cols-3 xl:grid-cols-7 h-[calc(100vh-6rem)]"
    >
      @for (column of boardColumns; track column.key) {
        <div
          class="flex flex-col gap-3 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]"
          [attr.data-column-key]="column.key"
          cdkDropList
          [id]="column.key"
          [cdkDropListData]="column.cards"
          [cdkDropListConnectedTo]="crmDropColumnIds"
          [cdkDropListDisabled]="!isCrmDropTarget(column.key)"
          (cdkDropListDropped)="onCrmDrop($event)"
        >
          <div class="sticky top-0 z-10 -mx-3 -mt-3 mb-1 rounded-t-lg bg-gray-50 px-3 py-2 dark:bg-white/[0.04]">
            <h3 class="flex items-center justify-between gap-2 text-sm font-semibold text-gray-800 dark:text-white/90">
              <span class="flex items-center gap-2">
                <app-badge [text]="column.title" [color]="columnBadgeColor(column.key)"></app-badge>
                <span class="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-white/[0.03] dark:text-white/80">
                  {{ visibleCardsFor(column).length }}
                </span>
              </span>
            </h3>

            @if (column.key === 'active-opportunity') {
              <!-- TVG-119: Sub-status filter pills, persisted in localStorage. -->
              <div
                class="mt-2 flex flex-wrap gap-1.5"
                role="group"
                aria-label="Filter by Active Opportunity sub-status"
                data-testid="sub-status-filter">
                @for (option of subStatusOptions; track option.value) {
                  <button
                    type="button"
                    class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
                    [ngClass]="{
                      'border-warning-300 bg-warning-50 text-warning-700 dark:border-warning-500/40 dark:bg-warning-500/15 dark:text-warning-300':
                        option.value === 'in_conversation' && isSubStatusSelected(option.value),
                      'border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-600 dark:bg-white/5 dark:text-white/80':
                        option.value === 'event_created' && isSubStatusSelected(option.value),
                      'border-dashed border-gray-300 bg-transparent text-gray-400 hover:text-gray-600 dark:border-gray-700 dark:text-gray-500 dark:hover:text-gray-300':
                        !isSubStatusSelected(option.value)
                    }"
                    [attr.aria-pressed]="isSubStatusSelected(option.value)"
                    [attr.data-testid]="'sub-status-filter-' + option.value"
                    (click)="toggleSubStatusFilter(option.value)">
                    {{ option.label }}
                  </button>
                }
              </div>
            }

            @if (column.key === 'lost') {
              <!-- TVG-124: Lost-column scope filter ("This quarter" / "All Lost"). -->
              <div
                class="mt-2 flex flex-wrap gap-1.5"
                role="group"
                aria-label="Filter Lost events"
                data-testid="lost-filter">
                @for (option of lostFilterOptions; track option.value) {
                  <button
                    type="button"
                    class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
                    [ngClass]="{
                      'border-gray-400 bg-gray-100 text-gray-700 dark:border-gray-500 dark:bg-white/10 dark:text-white/80':
                        isLostFilterSelected(option.value),
                      'border-dashed border-gray-300 bg-transparent text-gray-400 hover:text-gray-600 dark:border-gray-700 dark:text-gray-500 dark:hover:text-gray-300':
                        !isLostFilterSelected(option.value)
                    }"
                    [attr.aria-pressed]="isLostFilterSelected(option.value)"
                    [attr.data-testid]="'lost-filter-' + option.value"
                    (click)="selectLostFilter(option.value)">
                    {{ option.label }}
                  </button>
                }
              </div>
            }

            @if (column.key === 'cancelled') {
              <!-- TVG-123: Cancelled-column scope filter ("This year" / "All Cancelled"). -->
              <div
                class="mt-2 flex flex-wrap gap-1.5"
                role="group"
                aria-label="Filter Cancelled events"
                data-testid="cancelled-filter">
                @for (option of cancelledFilterOptions; track option.value) {
                  <button
                    type="button"
                    class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
                    [ngClass]="{
                      'border-gray-400 bg-gray-100 text-gray-700 dark:border-gray-500 dark:bg-white/10 dark:text-white/80':
                        isCancelledFilterSelected(option.value),
                      'border-dashed border-gray-300 bg-transparent text-gray-400 hover:text-gray-600 dark:border-gray-700 dark:text-gray-500 dark:hover:text-gray-300':
                        !isCancelledFilterSelected(option.value)
                    }"
                    [attr.aria-pressed]="isCancelledFilterSelected(option.value)"
                    [attr.data-testid]="'cancelled-filter-' + option.value"
                    (click)="selectCancelledFilter(option.value)">
                    {{ option.label }}
                  </button>
                }
              </div>
            }
          </div>

          @for (card of visibleCardsFor(column); track card) {
            @if (isContactCard(card)) {
              <!-- Pre-Quote card -->
              <div
                class="cursor-pointer rounded-lg border border-dashed border-gray-300 bg-white/70 p-3 shadow-sm transition hover:bg-white dark:border-gray-700 dark:bg-white/[0.03]"
                (click)="handleContactClick(card)"
              >
                <h4 class="mb-1 text-sm font-medium text-gray-800 dark:text-white/90">
                  {{ card.name }}
                </h4>
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <app-badge [text]="contactSourceLabel(card)" color="light"></app-badge>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ card.received_at | date: 'dd/MM/yyyy' }}
                  </span>
                </div>
              </div>
            } @else if (isQuoteCard(card)) {
              <!-- Quote Received card -->
              <div
                class="cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:bg-gray-50 dark:border-gray-800 dark:bg-white/5"
                (click)="handleQuoteCardClick(card)"
              >
                <div class="mb-2 flex items-start justify-between gap-2">
                  <h4 class="text-sm font-medium text-gray-800 dark:text-white/90">
                    {{ card.contact_name || card.contact_email }}
                  </h4>
                  <span
                    class="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full bg-gray-300"
                    title="Freshness indicator (see freshness model case)"
                  ></span>
                </div>
                <div class="mb-2 flex flex-wrap items-center gap-1.5">
                  @for (venue of card.venues; track venue.id) {
                    <app-badge [text]="venue.name" color="light"></app-badge>
                  }
                  @if (card.quote_count > 1) {
                    <app-badge [text]="card.quote_count + ' quotes'" color="info"></app-badge>
                  }
                </div>
                <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>{{ formatPriceRange(card.min_total, card.max_total) }}</span>
                  @if (daysSince(card.received_at) !== null) {
                    <span>{{ daysSince(card.received_at) }}d ago</span>
                  }
                </div>
              </div>
            } @else if (isOpportunityCard(card)) {
              <!-- TVG-118: Active Opportunity card (contact-level aggregate) -->
              @let oppFreshness = cardFreshnessColor(card, 'Opportunity');
              <div
                class="cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:bg-gray-50 dark:border-gray-800 dark:bg-white/5"
                (click)="handleOpportunityCardClick(card)"
                [attr.data-testid]="'opportunity-card-' + card.contact_id"
                cdkDrag
                [cdkDragData]="card"
                [cdkDragDisabled]="!isCardDraggable(card, column.key)"
              >
                <div class="mb-2 flex items-start justify-between gap-2">
                  <h4 class="text-sm font-medium text-gray-800 dark:text-white/90">
                    {{ card.contact_name }}
                  </h4>
                  @if (oppFreshness) {
                    <span
                      class="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                      [class.bg-green-500]="oppFreshness === 'green'"
                      [class.bg-amber-500]="oppFreshness === 'amber'"
                      [class.bg-gray-400]="oppFreshness === 'grey'"
                      [attr.aria-label]="'Prospect activity: ' + oppFreshness"
                      [attr.data-testid]="'freshness-dot-' + oppFreshness"
                    ></span>
                  }
                </div>
                @if (card.sub_status) {
                  <!-- TVG-119: Active Opportunity sub-status badge. -->
                  <div class="mb-2">
                    <app-badge
                      [text]="EventModelUtils.getSubStatusLabel(card.sub_status)"
                      [color]="EventModelUtils.getSubStatusColor(card.sub_status)"
                      data-testid="card-sub-status">
                    </app-badge>
                  </div>
                }
                <div class="mb-2 grid grid-cols-2 gap-1 text-xs text-gray-600 dark:text-gray-400">
                  <span>{{ card.events_count }} event{{ card.events_count === 1 ? '' : 's' }}</span>
                  <span>{{ card.quote_count }} quote{{ card.quote_count === 1 ? '' : 's' }}</span>
                </div>
                @if (card.venues.length > 0) {
                  <div class="mb-2 flex flex-wrap gap-1">
                    @for (venue of card.venues; track venue.id) {
                      <app-badge [text]="venue.name" color="light"></app-badge>
                    }
                  </div>
                }
                @if (card.last_sales_touch_at) {
                  <div class="text-xs text-gray-500 dark:text-gray-400" data-testid="last-sales-touch">
                    Last sales touch: {{ card.last_sales_touch_at | date: 'dd/MM/yyyy' }}
                  </div>
                }
                <!-- TVG-135: Open contact (primary) / Open event quick actions. -->
                <app-board-card-actions
                  [contactId]="card.contact_id"
                  [eventId]="card.event_id"
                  [primary]="cardPrimary(column.key)"
                  (openContact)="handleOpenContact(card.contact_id)"
                  (openEvent)="handleOpenEvent(card.event_id)"
                ></app-board-card-actions>
              </div>
            } @else if (isEventCard(card)) {
              @switch (column.key) {
                @case ('viewing-booked') {
                  <!-- TVG-121: Viewing Booked card — headline = viewing date/time/countdown -->
                  @let vbFreshness = cardFreshnessColor(card, card.status);
                  <div
                    class="cursor-pointer rounded-lg border border-l-4 border-gray-200 border-l-purple-500 bg-white p-3 shadow-sm transition hover:bg-gray-50 dark:border-gray-800 dark:bg-white/5"
                    (click)="handleEventCardClick(card, column.key)"
                    [attr.data-testid]="'viewing-booked-card-' + card.id"
                    cdkDrag
                    [cdkDragData]="card"
                    [cdkDragDisabled]="!isCardDraggable(card, column.key)"
                  >
                    <div class="mb-2 flex items-start justify-between gap-2">
                      <div>
                        <div class="text-sm font-semibold text-gray-800 dark:text-white/90" data-testid="viewing-date">
                          {{ card.show_round_at | date: 'EEE dd MMM' }}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                          {{ card.show_round_at | date: 'HH:mm' }}
                        </div>
                      </div>
                      <span
                        class="rounded-full px-2 py-0.5 text-xs font-medium"
                        [class.bg-red-100]="viewingCountdownUrgent(card)"
                        [class.text-red-700]="viewingCountdownUrgent(card)"
                        [class.bg-gray-100]="!viewingCountdownUrgent(card)"
                        [class.text-gray-700]="!viewingCountdownUrgent(card)"
                        data-testid="viewing-countdown"
                      >
                        {{ viewingCountdownLabel(card) }}
                      </span>
                    </div>
                    <div class="mt-2 space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                      <div class="font-medium text-gray-700 dark:text-white/80">{{ coupleNames(card) }}</div>
                      @if (card.venue) {
                        <div>{{ card.venue.name }}</div>
                      }
                      @if (card.sales_manager) {
                        <div>Sales: {{ card.sales_manager.name }}</div>
                      }
                      @if (card.start_date) {
                        <div>
                          <lucide-icon [name]="Calendar" class="mr-1 inline h-3.5 w-3.5"></lucide-icon>
                          {{ card.start_date | date: 'dd/MM/yyyy' }}
                        </div>
                      }
                    </div>
                    @if (card.last_sales_touch_at || vbFreshness) {
                      <div class="mt-2 flex items-center justify-between">
                        @if (card.last_sales_touch_at) {
                          <span class="text-xs text-gray-500 dark:text-gray-400" data-testid="last-sales-touch">
                            Last sales touch: {{ card.last_sales_touch_at | date: 'dd/MM/yyyy' }}
                          </span>
                        } @else {
                          <span></span>
                        }
                        @if (vbFreshness) {
                          <span
                            class="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                            [class.bg-green-500]="vbFreshness === 'green'"
                            [class.bg-amber-500]="vbFreshness === 'amber'"
                            [class.bg-gray-400]="vbFreshness === 'grey'"
                            [attr.data-testid]="'freshness-dot-' + vbFreshness"
                          ></span>
                        }
                      </div>
                    }
                    <app-board-card-actions
                      [contactId]="card.contact_id"
                      [eventId]="card.id"
                      [primary]="cardPrimary(column.key)"
                      (openContact)="handleOpenContact(card.contact_id)"
                      (openEvent)="handleOpenEvent(card.id)"
                    ></app-board-card-actions>
                  </div>
                }
                @case ('pencil-provisional') {
                  <!-- TVG-118: Pencil (Provisional) card -->
                  @let pFreshness = cardFreshnessColor(card, card.status);
                  <div
                    class="cursor-pointer rounded-lg border border-l-4 border-gray-200 border-l-blue-500 bg-white p-3 shadow-sm transition hover:bg-gray-50 dark:border-gray-800 dark:bg-white/5"
                    (click)="handleEventCardClick(card, column.key)"
                    [attr.data-testid]="'pencil-card-' + card.id"
                    cdkDrag
                    [cdkDragData]="card"
                    [cdkDragDisabled]="!isCardDraggable(card, column.key)"
                  >
                    <div class="mb-2 flex items-start justify-between gap-2">
                      <div>
                        @if (card.reference) {
                          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ card.reference }}</div>
                        }
                        <div class="text-xs text-gray-600 dark:text-gray-400">{{ coupleNames(card) }}</div>
                      </div>
                      @if (pFreshness) {
                        <span
                          class="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                          [class.bg-green-500]="pFreshness === 'green'"
                          [class.bg-amber-500]="pFreshness === 'amber'"
                          [class.bg-gray-400]="pFreshness === 'grey'"
                          [attr.data-testid]="'freshness-dot-' + pFreshness"
                        ></span>
                      }
                    </div>
                    <div class="space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                      @if (card.venue) {
                        <div>{{ card.venue.name }}</div>
                      }
                      <div>
                        <lucide-icon [name]="Calendar" class="mr-1 inline h-3.5 w-3.5"></lucide-icon>
                        {{ card.start_date | date: 'dd/MM/yyyy' }}
                      </div>
                      @if (guestTotal(card) !== null) {
                        <div>{{ guestTotal(card) }} guests</div>
                      }
                      @if (card.sales_manager) {
                        <div>Sales: {{ card.sales_manager.name }}</div>
                      }
                      @if (daysAtPencil(card) !== null) {
                        <div class="font-medium" data-testid="days-at-pencil">
                          {{ daysAtPencil(card) }} day{{ daysAtPencil(card) === 1 ? '' : 's' }} at Provisional
                        </div>
                      }
                    </div>
                    <app-board-card-actions
                      [contactId]="card.contact_id"
                      [eventId]="card.id"
                      [primary]="cardPrimary(column.key)"
                      (openContact)="handleOpenContact(card.contact_id)"
                      (openEvent)="handleOpenEvent(card.id)"
                    ></app-board-card-actions>
                  </div>
                }
                @case ('confirmed') {
                  <!-- TVG-118: Confirmed card (no freshness dot post-Confirmation) -->
                  <div
                    class="cursor-pointer rounded-lg border border-l-4 border-gray-200 border-l-emerald-500 bg-white p-3 shadow-sm transition hover:bg-gray-50 dark:border-gray-800 dark:bg-white/5"
                    (click)="handleEventCardClick(card, column.key)"
                    [attr.data-testid]="'confirmed-card-' + card.id"
                    cdkDrag
                    [cdkDragData]="card"
                    [cdkDragDisabled]="!isCardDraggable(card, column.key)"
                  >
                    <div class="mb-2 flex items-start justify-between gap-2">
                      <div>
                        @if (card.reference) {
                          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ card.reference }}</div>
                        }
                        <div class="text-xs text-gray-600 dark:text-gray-400">{{ coupleNames(card) }}</div>
                      </div>
                      <span
                        class="rounded-full px-2 py-0.5 text-xs font-medium"
                        [class.bg-red-100]="weddingCountdownUrgent(card)"
                        [class.text-red-700]="weddingCountdownUrgent(card)"
                        [class.bg-emerald-100]="!weddingCountdownUrgent(card)"
                        [class.text-emerald-700]="!weddingCountdownUrgent(card)"
                        data-testid="days-until-wedding"
                      >
                        {{ weddingCountdownLabel(card) }}
                      </span>
                    </div>
                    <div class="space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                      @if (card.venue) {
                        <div>{{ card.venue.name }}</div>
                      }
                      <div class="font-medium text-gray-700 dark:text-white/80">
                        <lucide-icon [name]="Calendar" class="mr-1 inline h-3.5 w-3.5"></lucide-icon>
                        {{ card.start_date | date: 'dd/MM/yyyy' }}
                      </div>
                      <div>Event: {{ card.event_manager?.name ?? 'Unassigned' }}</div>
                      @if (card.sales_manager) {
                        <div class="text-[11px] text-gray-500 dark:text-gray-400">
                          Sales: {{ card.sales_manager.name }}
                        </div>
                      }
                    </div>
                    <app-board-card-actions
                      [contactId]="card.contact_id"
                      [eventId]="card.id"
                      [primary]="cardPrimary(column.key)"
                      (openContact)="handleOpenContact(card.contact_id)"
                      (openEvent)="handleOpenEvent(card.id)"
                    ></app-board-card-actions>
                  </div>
                }
                @case ('completed') {
                  <!-- TVG-123: Completed card — Confirmed events whose wedding is >30 days past.
                       Status remains Confirmed in the DB; this is a query-time view. -->
                  <div
                    class="cursor-pointer rounded-lg border border-l-4 border-gray-200 border-l-gray-400 bg-white p-3 shadow-sm transition hover:bg-gray-50 dark:border-gray-800 dark:bg-white/5"
                    (click)="handleEventCardClick(card, column.key)"
                    [attr.data-testid]="'completed-card-' + card.id"
                  >
                    <div class="mb-2 flex items-start justify-between gap-2">
                      <div>
                        @if (card.reference) {
                          <div class="text-sm font-semibold text-gray-700 dark:text-white/80">{{ card.reference }}</div>
                        }
                        <div class="text-xs text-gray-600 dark:text-gray-400">{{ coupleNames(card) }}</div>
                      </div>
                    </div>
                    <div class="space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                      @if (card.venue) {
                        <div>{{ card.venue.name }}</div>
                      }
                      <div class="font-medium text-gray-700 dark:text-white/80">
                        <lucide-icon [name]="Calendar" class="mr-1 inline h-3.5 w-3.5"></lucide-icon>
                        {{ card.start_date | date: 'dd/MM/yyyy' }}
                      </div>
                      @if (card.event_manager) {
                        <div>Event: {{ card.event_manager.name }}</div>
                      }
                      @if (card.sales_manager) {
                        <div class="text-[11px] text-gray-500 dark:text-gray-400">
                          Sales: {{ card.sales_manager.name }}
                        </div>
                      }
                    </div>
                    <app-board-card-actions
                      [contactId]="card.contact_id"
                      [eventId]="card.id"
                      [primary]="cardPrimary(column.key)"
                      (openContact)="handleOpenContact(card.contact_id)"
                      (openEvent)="handleOpenEvent(card.id)"
                    ></app-board-card-actions>
                  </div>
                }
                @case ('cancelled') {
                  <!-- TVG-123: Cancelled card — post-Confirmation cancellation.
                       Muted styling, mirrors Lost. Surfaces cancelled_at. -->
                  <div
                    class="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-3 opacity-80 shadow-sm transition hover:bg-gray-100 dark:border-gray-800 dark:bg-white/[0.03]"
                    (click)="handleEventCardClick(card, column.key)"
                    [attr.data-testid]="'cancelled-card-' + card.id"
                  >
                    <h4 class="mb-1 text-sm font-medium text-gray-600 dark:text-gray-400">
                      {{ coupleNames(card) }}
                    </h4>
                    <div class="space-y-0.5 text-xs text-gray-500 dark:text-gray-500">
                      @if (card.venue) {
                        <div>{{ card.venue.name }}</div>
                      }
                      @if (card.start_date) {
                        <div>Wedding: {{ card.start_date | date: 'dd/MM/yyyy' }}</div>
                      }
                      @if (card.cancelled_at) {
                        <div data-testid="cancelled-date">Cancelled: {{ card.cancelled_at | date: 'dd/MM/yyyy' }}</div>
                      }
                      @if (card.sales_manager) {
                        <div>Sales: {{ card.sales_manager.name }}</div>
                      }
                    </div>
                    <app-board-card-actions
                      [contactId]="card.contact_id"
                      [eventId]="card.id"
                      [primary]="cardPrimary(column.key)"
                      (openContact)="handleOpenContact(card.contact_id)"
                      (openEvent)="handleOpenEvent(card.id)"
                    ></app-board-card-actions>
                  </div>
                }
                @case ('lost') {
                  <!-- TVG-118 / TVG-124: Lost card (muted, surfaces lost_at + lost_reason). -->
                  <div
                    class="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-3 opacity-80 shadow-sm transition hover:bg-gray-100 dark:border-gray-800 dark:bg-white/[0.03]"
                    (click)="handleEventCardClick(card, column.key)"
                    [attr.data-testid]="'lost-card-' + card.id"
                    cdkDrag
                    [cdkDragData]="card"
                    [cdkDragDisabled]="!isCardDraggable(card, column.key)"
                  >
                    <h4 class="mb-1 text-sm font-medium text-gray-600 dark:text-gray-400">
                      {{ coupleNames(card) }}
                    </h4>
                    <div class="space-y-0.5 text-xs text-gray-500 dark:text-gray-500">
                      @if (card.venue) {
                        <div>{{ card.venue.name }}</div>
                      }
                      @if (card.start_date) {
                        <div>Wedding: {{ card.start_date | date: 'dd/MM/yyyy' }}</div>
                      }
                      @if (card.lost_at) {
                        <div data-testid="lost-date">Lost: {{ card.lost_at | date: 'dd/MM/yyyy' }}</div>
                      }
                      @if (card.lost_reason) {
                        <div data-testid="lost-reason">Reason: {{ card.lost_reason }}</div>
                      }
                      @if (card.sales_manager) {
                        <div>Sales: {{ card.sales_manager.name }}</div>
                      }
                    </div>
                    <app-board-card-actions
                      [contactId]="card.contact_id"
                      [eventId]="card.id"
                      [primary]="cardPrimary(column.key)"
                      (openContact)="handleOpenContact(card.contact_id)"
                      (openEvent)="handleOpenEvent(card.id)"
                    ></app-board-card-actions>
                  </div>
                }
                @default {
                  <!-- Fallback event card (e.g. for unexpected status in viewing-booked) -->
                  <div
                    class="cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:bg-gray-50 dark:border-gray-800 dark:bg-white/5"
                    (click)="handleBoardEventClick(card)"
                  >
                    <h4 class="mb-2 text-sm font-medium text-gray-800 dark:text-white/90">{{ card.name }}</h4>
                    <div class="text-xs text-gray-500 dark:text-gray-400">{{ card.start_date | date: 'dd/MM/yyyy' }}</div>
                  </div>
                }
              }
            }
          } @empty {
            <div class="rounded-lg border border-dashed border-gray-200 px-3 py-6 text-center text-xs text-gray-400 dark:border-gray-700">
              No cards
            </div>
          }
        </div>
      }
    </div>

    <!-- TVG-134: capture dialog when dragging a card into Viewing Booked.
         Booking sets the viewing date; the override skips it (no state change). -->
    <app-viewing-date-modal
      [isOpen]="viewingModalOpen"
      [submitting]="viewingSubmitting"
      (confirmed)="submitViewingDate($event)"
      (skipped)="skipViewing()">
    </app-viewing-date-modal>

    <!-- TVG-331: "Book New Viewing" — pick the contact, pick the event (any
         venue), set the date. Books through the same endpoint as above. -->
    <app-book-new-viewing-modal
      [isOpen]="bookNewViewingOpen"
      [submitting]="bookNewViewingSubmitting"
      [loading]="bookNewViewingLoading"
      [contacts]="bookableContacts"
      (searchChanged)="onBookableSearch($event)"
      (confirmed)="submitBookNewViewing($event)"
      (cancelled)="closeBookNewViewing()">
    </app-book-new-viewing-modal>

    <!-- TVG-134: capture dialog when dragging a card into Lost. The reason is
         optional, so submitting with "No reason given" is the built-in override. -->
    <app-mark-as-lost-modal
      [isOpen]="lostModalOpen"
      [submitting]="lostSubmitting"
      (confirmed)="submitMarkAsLost($event)"
      (cancelled)="closeLostModal()">
    </app-mark-as-lost-modal>
  } @else {
    <!-- Legacy 5-column board with backlog -->
    <div
      class="flex-1 mt-7 grid grid-cols-1 border-t border-gray-200 dark:border-gray-800 sm:mt-0 sm:grid-cols-3 xl:grid-cols-6 gap-4 h-[calc(100vh-1rem)]"
    >
    <!-- Backlog Column -->
    <div
      class="backlog flex flex-col gap-5 p-4 xl:p-6 border-r-2 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 h-full"
    >
      <div class="mb-1 flex items-center justify-between">
        <h3 class="flex items-center gap-3 text-base font-medium text-gray-800 dark:text-white/90">
          <app-badge text="Not on Deals" color="light"></app-badge>
          <span class="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-theme-xs font-medium text-gray-700 dark:bg-white/[0.03] dark:text-white/80">
          {{ backlog.length }}
        </span>
        </h3>
      </div>

      @for (event of backlog; track event.id) {
        <div
          class="relative cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-theme-sm dark:border-gray-800 dark:bg-white/5"
          (click)="handleEventClick(event)"
        >
          <div>
            <div class="flex justify-between items-start gap-2 mb-4">
              <div class="flex items-center gap-2">
                @if (crmSalesFlowEnabled) {
                  @let freshness = EventModelUtils.getFreshnessColor(event);
                  @if (freshness) {
                    <span
                      class="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      [class.bg-green-500]="freshness === 'green'"
                      [class.bg-amber-500]="freshness === 'amber'"
                      [class.bg-gray-400]="freshness === 'grey'"
                      [attr.aria-label]="'Prospect activity: ' + freshness"
                      [attr.data-testid]="'freshness-dot-' + freshness"
                      [title]="event.last_prospect_activity_at
                        ? ('Last prospect activity ' + (event.last_prospect_activity_at | date: 'dd/MM/yyyy'))
                        : 'No prospect activity recorded'"
                    ></span>
                  }
                }
                <h4 class="text-base text-gray-800 dark:text-white/90">
                  {{ event.name }}
                </h4>
              </div>
              <lucide-icon
                [name]="SquareChevronRight"
                [title]="'Move to Deals'"
                class="flex-shrink-0 h-5 w-5 text-blue-500 cursor-pointer hover:text-blue-600"
                (click)="$event.stopPropagation();moveToBoard(event)"
              ></lucide-icon>
            </div>

            <div class="flex flex-col w-full justify-between gap-3">
              <span class="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                <lucide-icon [name]="Calendar" class="h-4 w-4 text-gray-400 dark:text-gray-500"></lucide-icon>
                {{ event.start_date | date: 'dd/MM/yyyy' }}
              </span>
              <div class="flex flex-wrap items-center gap-2">
                <app-badge [text]="event.priority_name" [color]="EventModelUtils.getPriorityColor(event.priority_name)"></app-badge>
                <app-badge [text]="event.status" [color]="EventModelUtils.getStatusColor(event.status)"></app-badge>
              </div>
            </div>

            @if (crmSalesFlowEnabled && EventModelUtils.showsLastSalesTouch(event)) {
              <div
                class="mt-3 text-xs text-gray-500 dark:text-gray-400"
                data-testid="last-sales-touch"
              >
                Last sales touch: {{ event.last_sales_touch_at | date: 'dd/MM/yyyy' }}
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Existing Swimlanes -->
    @for (lane of swimlanes; track lane.title) {
      <div
        class="swim-lane flex flex-col gap-5 p-4 xl:p-6 border-r border-gray-200 dark:border-gray-800 h-full"
        cdkDropList
        [id]="lane.title"
        [cdkDropListData]="lane.events"
        [cdkDropListConnectedTo]="swimlaneTitles"
        (cdkDropListDropped)="drop($event, lane.title)"
      >
        <div class="mb-1 flex items-center justify-between">
          <h3 class="flex items-center gap-3 text-base font-medium text-gray-800 dark:text-white/90">
            <app-badge [text]="lane.title" [color]="EventModelUtils.getStatusColor(lane.title)"></app-badge>
            <span class="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-theme-xs font-medium text-gray-700 dark:bg-white/[0.03] dark:text-white/80">
              {{ lane.events.length }}
            </span>
          </h3>
        </div>

        @for (event of lane.events; track event.id) {
          <div
            class="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-theme-sm dark:border-gray-800 dark:bg-white/5"
            cdkDrag
            (click)="handleEventClick(event)"
          >
            <div>
              <h4 class="mb-6 text-base text-gray-800 dark:text-white/90">
                {{ event.name }}
              </h4>

              <div class="flex flex-wrap w-full justify-between gap-3">
                <span class="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <lucide-icon [name]="Calendar" class="h-4 w-4 text-gray-400 dark:text-gray-500"></lucide-icon>
                  {{ event.start_date | date: 'dd/MM/yyyy' }}
                </span>
                <app-badge [text]="event.priority_name" [color]="EventModelUtils.getPriorityColor(event.priority_name)"></app-badge>
              </div>
            </div>
          </div>
        }
      </div>
    }
  </div>
  }

  <app-price-locking-warning-modal
    [isOpen]="showConfirmationModal"
    (confirmed)="confirmStatusChange()"
    (cancelled)="cancelStatusChange()">
  </app-price-locking-warning-modal>
</div>
